import React, { useState, useEffect, useMemo } from 'react';
import { FiSave, FiPackage, FiTag, FiHash, FiMapPin, FiTruck, FiAlertCircle, FiPlus } from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import AddCategoryModal from './AddCategoryModal';
import { DEFAULT_COMMON_HSN_CODES } from '../../utils/hsnCodes';

const InventoryForm = ({ show, onClose, onSave, editingItem = null, categories = [], onCategoryAdd, inventory = [] }) => {
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    category: '',
    cost_price: '',
    selling_price: '',
    quantity: '',
    unit: '',
    reorder_level: '',
    location: '',
    supplier: '',
    status: 'active',
    tags: [],
    hsn: '',
    gst_rate: ''
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showHsnDropdown, setShowHsnDropdown] = useState(false);
  const [customHsnCodes, setCustomHsnCodes] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('custom_hsn_codes');
    if (saved) {
      try {
        setCustomHsnCodes(JSON.parse(saved));
      } catch (e) {}
    }
  }, []);

  // Common Indian HSN/SAC Codes pre-loaded for convenience
  const COMMON_HSN_CODES = useMemo(() => [
    ...customHsnCodes,
    ...DEFAULT_COMMON_HSN_CODES
  ], [customHsnCodes]);

  // Extract unique HSN codes from inventory history for smart auto-complete
  const hsnHistory = useMemo(() => {
    const historyHsns = [];
    if (inventory && inventory.length > 0) {
      inventory.forEach(item => {
        if (item.hsn && !COMMON_HSN_CODES.find(c => c.code === item.hsn)) {
          historyHsns.push(item.hsn);
        }
      });
    }
    return [...new Set(historyHsns)];
  }, [inventory, COMMON_HSN_CODES]);

  // Combine and filter HSN list based on user search (supports code, name, category, and keywords like tv, ac, cooler, rice, dal)
  const filteredHsnList = useMemo(() => {
    const allOptions = [
      ...COMMON_HSN_CODES,
      ...hsnHistory.map(code => ({ code, label: 'Custom Used Code', category: 'Custom' }))
    ];
    
    // Remove duplicates based on code
    const uniqueOptions = Array.from(new Map(allOptions.map(item => [item.code, item])).values());
    
    if (!formData.hsn) return uniqueOptions;
    
    const searchLower = formData.hsn.toLowerCase().trim();
    return uniqueOptions.filter(item => 
      (item.code && item.code.toLowerCase().includes(searchLower)) || 
      (item.label && item.label.toLowerCase().includes(searchLower)) ||
      (item.category && item.category.toLowerCase().includes(searchLower)) ||
      (item.keywords && item.keywords.toLowerCase().includes(searchLower))
    );
  }, [formData.hsn, COMMON_HSN_CODES, hsnHistory]);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        sku: editingItem.sku || '',
        name: editingItem.name || '',
        description: editingItem.description || '',
        category: editingItem.category_name || editingItem.category || '',
        cost_price: editingItem.cost_price || '',
        selling_price: editingItem.selling_price || '',
        quantity: editingItem.quantity || '',
        unit: editingItem.unit || editingItem.unit_name || '',
        reorder_level: editingItem.reorder_level || '',
        location: editingItem.location || '',
        supplier: editingItem.supplier || '',
        status: editingItem.status || 'active',
        tags: editingItem.tags || [],
        hsn: editingItem.hsn || '',
        gst_rate: editingItem.gst_rate || ''
      });
    } else {
      setFormData({
        sku: '',
        name: '',
        description: '',
        category: '',
        cost_price: '',
        selling_price: '',
        quantity: '',
        unit: '',
        reorder_level: '',
        location: '',
        supplier: '',
        status: 'active',
        tags: [],
        hsn: '',
        gst_rate: ''
      });
    }
    setErrors({});
  }, [editingItem, show]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.category.trim()) newErrors.category = 'Category is required';
    if (!formData.cost_price || parseFloat(formData.cost_price) <= 0) newErrors.cost_price = 'Valid cost price is required';
    if (!formData.selling_price || parseFloat(formData.selling_price) <= 0) newErrors.selling_price = 'Valid selling price is required';

    // Quantity validation - must be whole positive number
    const quantity = formData.quantity;
    if (quantity !== '' && quantity !== undefined) {
      const quantityNum = Number(quantity);
      if (isNaN(quantityNum)) {
        newErrors.quantity = 'Quantity must be a valid number';
      } else if (quantityNum < 0) {
        newErrors.quantity = 'Quantity cannot be negative';
      } else if (!Number.isInteger(quantityNum)) {
        newErrors.quantity = 'Quantity must be a whole number (no decimals)';
      }
    }

    // Reorder level validation - must be whole positive number
    const reorderLevel = formData.reorder_level;
    if (reorderLevel !== '' && reorderLevel !== undefined) {
      const reorderNum = Number(reorderLevel);
      if (isNaN(reorderNum)) {
        newErrors.reorder_level = 'Reorder level must be a valid number';
      } else if (reorderNum < 0) {
        newErrors.reorder_level = 'Reorder level cannot be negative';
      } else if (!Number.isInteger(reorderNum)) {
        newErrors.reorder_level = 'Reorder level must be a whole number (no decimals)';
      }
    }

    if (!formData.unit.trim()) newErrors.unit = 'Unit is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);
    try {
      // Convert string values to numbers for price fields and remove unused fields
      const { sku, ...cleanFormData } = formData;
      const submitData = {
        ...cleanFormData,
        ...(formData.sku && formData.sku.trim() ? { sku: formData.sku.trim() } : {}),
        cost_price: Math.max(0, parseInt(formData.cost_price) || 0),
        selling_price: Math.max(0, parseInt(formData.selling_price) || 0),
        quantity: Math.max(0, Math.floor(Number(formData.quantity) || 0)),
        reorder_level: Math.max(0, Math.floor(Number(formData.reorder_level) || 0)),
        unit: formData.unit,
        gst_rate: parseFloat(formData.gst_rate) || 18 // Default to 18% if not provided
      };

      await onSave(submitData);
      handleClose();
    } catch (error) {
      console.error('Error saving item:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // For all numeric fields, only allow integer values (no decimals)
    if (name === 'quantity' || name === 'reorder_level' || name === 'cost_price' || name === 'selling_price') {
      // Remove any non-digit characters (including decimal point, minus sign, etc.)
      const integerValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: integerValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTagsChange = (e) => {
    const tagsString = e.target.value;
    const tagsArray = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFormData(prev => ({ ...prev, tags: tagsArray }));
  };

  const getTagsDisplay = () => {
    return Array.isArray(formData.tags) ? formData.tags.join(', ') : '';
  };

  const handleCategoryAdd = async (categoryData) => {
    try {
      const newCategory = await onCategoryAdd(categoryData);
      if (newCategory) {
        setFormData(prev => ({
          ...prev,
          category: newCategory.name || newCategory
        }));
      }
    } catch (error) {
      console.error('Error adding category:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      sku: '',
      name: '',
      description: '',
      category: '',
      cost_price: '',
      selling_price: '',
      quantity: '',
      reorder_level: '',
      location: '',
      supplier: '',
      status: 'active',
      tags: []
    });
    setErrors({});
    setSaving(false);
    onClose();
  };

  if (!show) return null;

  return (
    <>
      <div className="custom-modal-backdrop" onClick={handleClose}></div>
      <div className="custom-modal add-inventory-modal">
        <button className="close-btn" onClick={handleClose}>&times;</button>
        <h2>{editingItem ? 'Edit Item' : 'Add New Item'}</h2>

        <form onSubmit={handleSubmit} className="add-modal-form">
          {/* SKU and Name Row */}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                <FiHash className="label-icon" />
                SKU
              </label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                className={`form-input`}
                placeholder="Enter SKU"
                disabled={!!editingItem} // SKU cannot be changed after creation
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiPackage className="label-icon" />
                Item Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`form-input ${errors.name ? 'error' : ''}`}
                placeholder="Enter item name"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">
              <FiTag className="label-icon" />
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="form-input form-textarea"
              placeholder="Enter item description..."
              rows="3"
            />
          </div>

          {/* Category and Status Row */}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                <FiTag className="label-icon" />
                Category *
              </label>
              <div className="category-select-group">
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`form-input ${errors.category ? 'error' : ''}`}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id || category} value={category.name || category}>
                      {category.name || category}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm add-category-btn"
                  onClick={() => setShowAddCategory(true)}
                  title="Add New Category"
                >
                  <FiPlus />
                </button>
              </div>
              {errors.category && <span className="error-message">{errors.category}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiAlertCircle className="label-icon" />
                Status
              </label>
              <select name="status" value={formData.status} onChange={handleChange} className="form-input">
                <option value="active">Active</option>
                <option value="low_stock">Low Stock</option>
                <option value="out_of_stock">Out of Stock</option>
                <option value="discontinued">Discontinued</option>
              </select>
            </div>
          </div>

          {/* Unit Row */}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                <FiPackage className="label-icon" />
                Unit *
              </label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className={`form-input ${errors.unit ? 'error' : ''}`}
              >
                <option value="">Select Unit</option>
                <option value="NOS">NOS</option>
                <option value="Piece">Piece</option>
                <option value="KG">KG</option>
                <option value="Gram">Gram</option>
                <option value="Ltr">Ltr</option>
                <option value="ML">ML</option>
                <option value="Mtr">Mtr</option>
                <option value="CM">CM</option>
                <option value="Box">Box</option>
                <option value="Dozen">Dozen</option>
                <option value="Pack">Pack</option>
                <option value="Set">Set</option>
                <option value="Pair">Pair</option>
                <option value="Meter">Meter</option>
                <option value="Liter">Liter</option>
                <option value="Kilogram">Kilogram</option>
                <option value="Ton">Ton</option>
                <option value="Gallon">Gallon</option>
                <option value="Sq Ft">Sq Ft</option>
                <option value="Sq Mtr">Sq Mtr</option>
                <option value="Cubic Ft">Cubic Ft</option>
                <option value="Cubic Mtr">Cubic Mtr</option>
              </select>
              {errors.unit && <span className="error-message">{errors.unit}</span>}
            </div>
          </div>

          {/* Price Row */}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                <FaRupeeSign className="label-icon" />
                Cost Price (₹) *
              </label>
              <input
                type="number"
                name="cost_price"
                value={formData.cost_price}
                onChange={handleChange}
                className={`form-input ${errors.cost_price ? 'error' : ''}`}
                placeholder="0"
                step="1"
                min="0"
              />
              {errors.cost_price && <span className="error-message">{errors.cost_price}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FaRupeeSign className="label-icon" />
                Selling Price (₹) *
              </label>
              <input
                type="number"
                name="selling_price"
                value={formData.selling_price}
                onChange={handleChange}
                className={`form-input ${errors.selling_price ? 'error' : ''}`}
                placeholder="0"
                step="1"
                min="0"
              />
              {errors.selling_price && <span className="error-message">{errors.selling_price}</span>}
            </div>
          </div>

          {/* Profit Display */}
          <div className="form-group">
            <label className="form-label">
              <FaRupeeSign className="label-icon" />
              Profit per Unit (₹)
            </label>
            <input
              type="text"
              value={formData.selling_price && formData.cost_price ?
                `₹${Math.max(0, parseInt(formData.selling_price || 0) - parseInt(formData.cost_price || 0))}` : '₹0'}
              className="form-input"
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
            <small className="form-help">Auto-calculated: Selling Price - Cost Price</small>
          </div>

          {/* HSN and GST Rate Row */}
          <div className="form-grid">
            <div className="form-group" style={{ position: 'relative', zIndex: showHsnDropdown ? 9999 : 1 }}>
              <label className="form-label">
                <FiHash className="label-icon" />
                HSN Code
              </label>
              <input
                type="text"
                name="hsn"
                value={formData.hsn}
                onChange={handleChange}
                onFocus={() => setShowHsnDropdown(true)}
                onBlur={() => setTimeout(() => setShowHsnDropdown(false), 200)}
                className="form-input"
                placeholder="Search or enter HSN code"
                autoComplete="off"
              />
              
              {showHsnDropdown && (
                <div 
                  className="hsn-dropdown-menu rounded shadow"
                  style={{ 
                    position: 'absolute', 
                    top: '100%', 
                    left: 0, 
                    width: '100%', 
                    maxHeight: '260px', 
                    overflowY: 'auto', 
                    zIndex: 99999,
                    marginTop: '4px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  {filteredHsnList.length > 0 ? (
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {filteredHsnList.map((item, index) => (
                        <li 
                          key={index}
                          style={{ 
                            padding: '10px 14px',
                            cursor: 'pointer', 
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: 'white',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseDown={() => {
                            setFormData(prev => ({ 
                              ...prev, 
                              hsn: item.code
                            }));
                            setShowHsnDropdown(false);
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>{item.code}</span>
                            {item.category && (
                              <span style={{ 
                                fontSize: '11px', 
                                padding: '1px 7px', 
                                borderRadius: '10px', 
                                background: item.category === 'Grocery' ? '#ecfdf5' : item.category === 'Electricals' ? '#eff6ff' : '#f3f4f6', 
                                color: item.category === 'Grocery' ? '#047857' : item.category === 'Electricals' ? '#1d4ed8' : '#374151',
                                fontWeight: '500' 
                              }}>
                                {item.category}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '3px', lineHeight: '1.4' }}>{item.label}</div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div style={{ padding: '12px 15px', fontSize: '13px', color: '#777' }}>
                      Press enter to use "{formData.hsn}"
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FaRupeeSign className="label-icon" />
                GST Rate (%)
              </label>
              <input
                type="number"
                name="gst_rate"
                value={formData.gst_rate}
                onChange={handleChange}
                className="form-input"
                placeholder="18"
                step="0.01"
                min="0"
                max="100"
              />
            </div>
          </div>

          {/* Stock Row */}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                <FiPackage className="label-icon" />
                Quantity
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className={`form-input ${errors.quantity ? 'error' : ''}`}
                placeholder="0"
                step="1"
                min="0"
              />
              {errors.quantity && <span className="error-message">{errors.quantity}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiAlertCircle className="label-icon" />
                Reorder Level
              </label>
              <input
                type="number"
                name="reorder_level"
                value={formData.reorder_level}
                onChange={handleChange}
                className={`form-input ${errors.reorder_level ? 'error' : ''}`}
                placeholder="0"
                step="1"
                min="0"
              />
              {errors.reorder_level && <span className="error-message">{errors.reorder_level}</span>}
            </div>
          </div>

          {/* Location and Supplier Row */}
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                <FiMapPin className="label-icon" />
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter storage location"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiTruck className="label-icon" />
                Supplier
              </label>
              <input
                type="text"
                name="supplier"
                value={formData.supplier}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter supplier name"
              />
            </div>
          </div>

          {/* Tags */}
          <div className="form-group">
            <label className="form-label">
              <FiTag className="label-icon" />
              Tags
            </label>
            <input
              type="text"
              name="tags"
              value={getTagsDisplay()}
              onChange={handleTagsChange}
              className="form-input"
              placeholder="Enter tags separated by commas (e.g., premium, featured, sale)"
            />
            <small className="form-help">Separate multiple tags with commas</small>
          </div>

          {/* Form Actions */}
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              <FiSave /> {saving ? 'Saving...' : (editingItem ? 'Update Item' : 'Save Item')}
            </button>
          </div>
        </form>
      </div>

      {/* Add Category Modal */}
      <AddCategoryModal
        show={showAddCategory}
        onClose={() => setShowAddCategory(false)}
        onSave={handleCategoryAdd}
        existingCategories={categories}
      />
    </>
  );
};

export default InventoryForm;
